import { window } from '../window.js';

/**
 * Collapsible text control.
 */
class CollapsibleText extends window.HTMLElement {
  connectedCallback() {
    this.constructor.resizeObserver.observe(this);
    if (this.hasAttribute('value')) {
      this.#setValue(this.value);
    }
  }

  disconnectedCallback() {
    this.constructor.resizeObserver.unobserve(this);
  }

  get maxLineCount() {
    return parseInt(this.getAttribute('max-line-count')) || 5;
  }

  get label() {
    return this.getAttribute('label');
  }

  get value() {
    return this.getAttribute('value');
  }

  set value(v) {
    this.classList.remove('expanded');
    this.setAttribute('value', v);
    this.#setValue(v);
  }

  isExpanded() {
    return this.classList.contains('expanded');
  }

  toggle() {
    this.isExpanded() ? this.collapse() : this.expand();
  }

  collapse() {
    this.classList.remove('expanded');
    this.style.maxHeight = `${this.textHeight}px`;
    this.insertBefore(this.#moreElement, this.firstHiddenSpan);
    for (
      let child = this.lastElementChild;
      child != this.#moreElement;
      child = child.previousElementSibling
    ) {
      child.hidden = true;
    }
  }

  expand() {
    this.classList.add('expanded');
    this.style.maxHeight = `unset`;
    for (
      let child = this.lastElementChild;
      child != this.#moreElement;
      child = child.previousElementSibling
    ) {
      child.hidden = false;
    }
    this.removeChild(this.#moreElement);
  }

  adjust() {
    if (this.offsetParent === null || this.isExpanded()) {
      return;
    }

    let rect = this.getBoundingClientRect();

    let last_line_bottom = 0,
      line_count = 0,
      max_line_count = this.maxLineCount,
      prev_span = null;

    for (let span of this.children) {
      let span_rect = span.getBoundingClientRect();
      if (last_line_bottom < span_rect.bottom) {
        line_count++;
        last_line_bottom = span_rect.bottom;
        if (line_count == max_line_count) {
          this.textHeight = span_rect.bottom - rect.top;
        } else if (line_count > max_line_count) {
          this.firstHiddenSpan = prev_span;
          this.collapse();
          this.#moreElement.hidden = false;
          return;
        }
      }
      prev_span = span;
      prev_span.hidden = false;
    }

    this.#moreElement.hidden = true;
  }

  #setValue(v) {
    let labelMarkup = this.label
      ? `<span class='label'>${this.label}</span> `
      : '';

    let valueMarkup = '';
    let words = v.match(/\S+\s*/g) || [];
    for (let word of words) {
      valueMarkup += `<span>${word} </span>`;
    }

    this.innerHTML = `${labelMarkup}${valueMarkup}`;
  }

  get #moreElement() {
    if (!this.#moreEl) {
      this.#moreEl = document.createElement('span');
      this.#moreEl.className = 'more link';
      this.#moreEl.textContent = ' …';
      this.#moreEl.setAttribute('title', 'More');
      this.#moreEl.addEventListener('click', () => this.toggle());
    }
    return this.#moreEl;
  }

  static get resizeObserver() {
    if (!this.#resizeObserver) {
      this.#resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          entry.target.adjust();
        }
      });
    }
    return this.#resizeObserver;
  }

  static #resizeObserver;

  #moreEl;
}

window.customElements.define('collapsible-text', CollapsibleText);

export { CollapsibleText };
